import * as fastSafeStringify from 'fast-safe-stringify';
/**
 * Prints an error to a string
 * @param error - The error to print
 * @returns The error as a string
 */
export function printError(error) {
    if (typeof error === 'string') {
        return error;
    }
    if (error &&
        typeof error === 'object' &&
        'stack' in error &&
        'message' in error &&
        typeof error.message === 'string') {
        return error.message;
    }
    if (typeof error === 'object') {
        return fastSafeStringify.default(error);
    }
    return String(error);
}
/**
 * Detects browser-level fetch failures surfaced by Supabase clients.
 *
 * These are transient connectivity failures and should usually be logged as
 * warnings. The check is intentionally narrow so unrelated TypeErrors still
 * remain on the error path.
 */
export function isBrowserNetworkError(error) {
    const message = getErrorMessage(error);
    return /^(TypeError:\s*)?(Failed to fetch|Load failed|NetworkError\b)/.test(message);
}
function getErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    if (error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string') {
        return error.message;
    }
    return typeof error === 'string' ? error : '';
}
/** PostgREST wire code for JWT `iat` slightly ahead of the server clock. */
export const POSTGREST_JWT_ISSUED_AT_FUTURE_CODE = 'PGRST303';
/**
 * Detects PostgREST PGRST303 ("JWT issued at future") errors, including Angular
 * / Promise rejection wrappers (`rejection`, `cause`, `ngOriginalError`,
 * `originalError`). Used by portal GlobalErrorHandlers to skip Sentry capture
 * for this transient clock-skew condition (LEA-295).
 */
export function isPostgrestJwtIssuedAtFutureError(error) {
    return isPostgrestJwtIssuedAtFutureErrorWithVisited(error, new Set());
}
function isPostgrestJwtIssuedAtFutureErrorWithVisited(error, visited) {
    if ((typeof error === 'object' && error !== null) || typeof error === 'function') {
        if (visited.has(error)) {
            return false;
        }
        visited.add(error);
    }
    if (hasPostgrestJwtIssuedAtFutureCode(error)) {
        return true;
    }
    const errorLike = error;
    const wrappedErrors = [
        errorLike === null || errorLike === void 0 ? void 0 : errorLike.rejection,
        errorLike === null || errorLike === void 0 ? void 0 : errorLike.cause,
        errorLike === null || errorLike === void 0 ? void 0 : errorLike.ngOriginalError,
        errorLike === null || errorLike === void 0 ? void 0 : errorLike.originalError,
    ];
    return wrappedErrors.some((wrappedError) => wrappedError != null &&
        isPostgrestJwtIssuedAtFutureErrorWithVisited(wrappedError, visited));
}
function hasPostgrestJwtIssuedAtFutureCode(error) {
    if (!error || typeof error !== 'object') {
        return false;
    }
    return error.code === POSTGREST_JWT_ISSUED_AT_FUTURE_CODE;
}
